import React from "react";

import { DropdownMenuProps } from "../DropdownMenu";

const getHTMLElementHeight = (element: HTMLElement): number => {
  if (!element) {
    return 0;
  }
  const rect = element.getBoundingClientRect();
  const computedStyle = getComputedStyle(element);
  const marginTop = parseFloat(computedStyle.marginTop) || 0;
  const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
  return rect.height + marginTop + marginBottom;
};

export function useDynamicDropdownHeight({
  dropdownOptions,
  dropdownRef,
  optionDisplayCount,
  showNoResults
}: {
  dropdownOptions: DropdownMenuProps["dropdownOptions"];
  dropdownRef: React.RefObject<HTMLUListElement>;
  optionDisplayCount: number | null;
  showNoResults?: boolean;
}) {
  const hasScroll =
    !!optionDisplayCount && dropdownOptions.length > optionDisplayCount;

  // Use optionDisplayCount to restrict the height of the dropdown.
  // If the number of options exceeds optionDisplayCount, the dropdown
  // will have a scrollbar.
  React.useEffect(() => {
    if (!dropdownRef.current) {
      return;
    }

    if (!optionDisplayCount || dropdownOptions.length === 0 || showNoResults) {
      dropdownRef.current.style.setProperty("max-height", null);
      return;
    }

    const firstOption =
      dropdownRef.current.querySelector<HTMLLIElement>("li:first-of-type");
    const firstOptionHeight = firstOption
      ? getHTMLElementHeight(firstOption)
      : 0;
    const lastOption =
      dropdownRef.current.querySelector<HTMLLIElement>("li:last-of-type");
    const lastOptionHeight = lastOption ? getHTMLElementHeight(lastOption) : 0;
    const option = dropdownRef.current.querySelector<HTMLLIElement>(
      "li:not(:first-of-type):not(:last-of-type)"
    );
    const optionHeight = option ? getHTMLElementHeight(option) : 0;

    const displayCount = Math.min(dropdownOptions.length, optionDisplayCount);

    let dropdownHeight = 0;
    if (displayCount === 1) {
      dropdownHeight = firstOptionHeight;
    } else {
      const totalOptionHeight = optionHeight * (optionDisplayCount - 2); // excluding first and last option

      dropdownHeight = firstOptionHeight + lastOptionHeight + totalOptionHeight;
    }

    dropdownRef.current.style.setProperty("max-height", `${dropdownHeight}px`);
  }, [dropdownRef, optionDisplayCount, dropdownOptions, showNoResults]);

  const handleScroll = React.useCallback(() => {
    if (!dropdownRef.current) {
      return;
    }
  }, [dropdownRef]);

  // For the initial render, check if the dropdown has overflow
  // and show bottom overlay if needed
  React.useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  return { handleScroll, hasScroll };
}
